/**
 * Componente: Medidor Interactivo de Robustez y Entropía de Contraseña
 */

'use client';

import React, { useMemo } from 'react';
import { Check, X } from 'lucide-react';

interface PasswordEntropyMeterProps {
  password: string;
}

export function PasswordEntropyMeter({ password }: PasswordEntropyMeterProps) {
  const analysis = useMemo(() => {
    const hasMinLength = password.length >= 8;
    const hasLetters = /[a-zA-Z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    const hasSymbols = /[^a-zA-Z0-9]/.test(password);
    const hasUpperAndLower = /[a-z]/.test(password) && /[A-Z]/.test(password);

    let score = 0;
    if (hasMinLength) score++;
    if (hasLetters && hasNumbers) score++;
    if (hasUpperAndLower) score++;
    if (hasSymbols || password.length >= 12) score++;

    let label = 'Muy débil';
    let colorClass = 'bg-red-500';

    if (score === 1) {
      label = 'Débil';
      colorClass = 'bg-orange-500';
    } else if (score === 2) {
      label = 'Aceptable';
      colorClass = 'bg-yellow-500';
    } else if (score === 3) {
      label = 'Buena';
      colorClass = 'bg-[#78A83B]';
    } else if (score >= 4) {
      label = 'Excelente';
      colorClass = 'bg-emerald-600';
    }

    return {
      score,
      label,
      colorClass,
      hasMinLength,
      hasLetters,
      hasNumbers,
      hasSymbols,
    };
  }, [password]);

  if (!password) {
    return (
      <div className="text-xs text-gray-500 mt-1">
        Usa 8 o más caracteres con una combinación de letras y números.
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2">
      {/* Barra de progreso de entropía */}
      <div className="flex items-center gap-1.5">
        {[1, 2, 3, 4].map((level) => (
          <div
            key={level}
            className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
              analysis.score >= level ? analysis.colorClass : 'bg-gray-200'
            }`}
          />
        ))}
        <span className="text-xs font-semibold text-gray-600 ml-1.5 min-w-[70px]">
          {analysis.label}
        </span>
      </div>

      {/* Requisitos mínimos */}
      <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px] text-gray-600">
        <div className="flex items-center gap-1">
          {analysis.hasMinLength ? (
            <Check size={12} className="text-emerald-600 stroke-[3]" />
          ) : (
            <X size={12} className="text-gray-400 stroke-[2]" />
          )}
          <span className={analysis.hasMinLength ? 'text-gray-800' : 'text-gray-400'}>
            Mínimo 8 caracteres
          </span>
        </div>

        <div className="flex items-center gap-1">
          {analysis.hasLetters ? (
            <Check size={12} className="text-emerald-600 stroke-[3]" />
          ) : (
            <X size={12} className="text-gray-400 stroke-[2]" />
          )}
          <span className={analysis.hasLetters ? 'text-gray-800' : 'text-gray-400'}>
            Al menos una letra
          </span>
        </div>

        <div className="flex items-center gap-1">
          {analysis.hasNumbers ? (
            <Check size={12} className="text-emerald-600 stroke-[3]" />
          ) : (
            <X size={12} className="text-gray-400 stroke-[2]" />
          )}
          <span className={analysis.hasNumbers ? 'text-gray-800' : 'text-gray-400'}>
            Al menos un número
          </span>
        </div>

        <div className="flex items-center gap-1">
          {analysis.hasSymbols ? (
            <Check size={12} className="text-emerald-600 stroke-[3]" />
          ) : (
            <span className="w-3" />
          )}
          <span className={analysis.hasSymbols ? 'text-gray-800' : 'text-gray-400'}>
            Símbolo especial (opcional)
          </span>
        </div>
      </div>
    </div>
  );
}
